import React, { ComponentType, CSSProperties } from 'react';

import { DefaultFilePreview } from './DefaultFilePreview';
import type { DropzoneContent, DropzoneExtra, DropzonePreviewProps, DropzoneStyles, FileWithMeta } from './types';
import { useFileUploads, UseFileUploadsOptions } from './useFileUploads';

export interface FileDropzoneProps extends UseFileUploadsOptions {
  /** The label shown while no file is in. */
  inputContent?: DropzoneContent;
  /** The label shown once a file is in. */
  inputWithFilesContent?: DropzoneContent;
  /** `null` renders no previews; left out, each file renders `DefaultFilePreview`. */
  PreviewComponent?: ComponentType<DropzonePreviewProps> | null;
  /** Inline styles merged over the `dzu-*` classes. */
  styles?: DropzoneStyles;
}

const resolve = (content: DropzoneContent, files: FileWithMeta[], extra: DropzoneExtra) =>
  typeof content === 'function' ? content(files, extra) : content;

/**
 * A drop area with a file input inside a label, one preview per file, and the `dzu-*` class names
 * `injectFileDropzoneStyles` styles. Hides the input once `maxFiles` files are in.
 */
export const FileDropzone = ({
  inputContent = 'Drag Files or Click to Browse',
  inputWithFilesContent = 'Add Files',
  PreviewComponent,
  styles = {},
  ...options
}: FileDropzoneProps): JSX.Element => {
  const { files, extra, rootProps, inputProps } = useFileUploads(options);
  const hasFiles = files.length > 0;

  let className = 'dzu-dropzone';
  let style: CSSProperties | undefined = styles.dropzone;
  if (extra.reject) {
    className = `${className} dzu-dropzoneActive`;
    style = { ...(style || {}), ...(styles.dropzoneReject || {}) };
  } else if (extra.active) {
    className = `${className} dzu-dropzoneActive`;
    style = { ...(style || {}), ...(styles.dropzoneActive || {}) };
  }

  const Preview = PreviewComponent || DefaultFilePreview;

  return (
    <div
      ref={rootProps.ref as React.RefObject<HTMLDivElement>}
      className={className}
      style={style}
      onDragEnter={rootProps.onDragEnter}
      onDragOver={rootProps.onDragOver}
      onDragLeave={rootProps.onDragLeave}
      onDrop={rootProps.onDrop}
    >
      {PreviewComponent !== null &&
        files.map(f => (
          <Preview
            className="dzu-previewContainer"
            imageClassName="dzu-previewImage"
            style={styles.preview}
            imageStyle={styles.previewImage}
            key={f.meta.id}
            fileWithMeta={f}
            meta={{ ...f.meta }}
            isUpload={Boolean(options.getUploadParams)}
            canCancel
            canRemove
            canRestart
            files={files}
            extra={extra}
          />
        ))}
      {files.length < extra.maxFiles && (
        <label
          className={hasFiles ? 'dzu-inputLabelWithFiles' : 'dzu-inputLabel'}
          style={hasFiles ? styles.inputLabelWithFiles : styles.inputLabel}
        >
          {resolve(hasFiles ? inputWithFilesContent : inputContent, files, extra)}
          <input
            ref={inputProps.ref}
            className="dzu-input"
            style={styles.input}
            type="file"
            accept={extra.accept}
            multiple={extra.multiple}
            onChange={inputProps.onChange}
          />
        </label>
      )}
    </div>
  );
};
